import { Link } from '@tanstack/react-router'
import type { ProjectApCostSummary, ProjectCostLink } from '@/api/hooks'
import { formatMoney } from '@/lib/utils'
import { AmountList } from '@/features/projects/amount-list'

function InvoiceLink({ invoice }: { invoice: ProjectCostLink }) {
  return (
    <Link
      to="/invoices/$invoiceId"
      params={{ invoiceId: invoice.invoice_uuid }}
      className="min-w-0 truncate text-primary hover:underline"
    >
      {invoice.vendor_name || 'Vendor'} · {invoice.invoice_number || 'No invoice #'}
    </Link>
  )
}

function CostRow({
  label,
  amount,
  invoices,
}: {
  label: string
  amount: string | number
  invoices: ProjectCostLink[]
}) {
  return (
    <div className="space-y-0.5 text-xs">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums font-medium">{formatMoney(amount)}</span>
      </div>
      {invoices.map((invoice) => (
        <div key={invoice.invoice_uuid} className="flex items-baseline justify-between gap-2">
          <InvoiceLink invoice={invoice} />
          {invoices.length > 1 && (
            <span className="shrink-0 tabular-nums text-muted-foreground">{formatMoney(invoice.amount)}</span>
          )}
        </div>
      ))}
    </div>
  )
}

/** Quoted vs actual freight, plus install and storage, inside the project's AP card. */
export function ProjectFreightCosts({ costs }: { costs: ProjectApCostSummary }) {
  const { freight } = costs
  const hasFreight = freight.shipments.length > 0 || freight.project_invoices.length > 0
  const install = costs.install_storage_invoices.filter((invoice) => invoice.expense_category === 'installation')
  const storage = costs.install_storage_invoices.filter((invoice) => invoice.expense_category === 'storage')
  if (!hasFreight && install.length === 0 && storage.length === 0) return null

  const freightProfit = Number(freight.quoted_amount) - Number(freight.cost_amount)

  return (
    <div className="space-y-3 border-t pt-3">
      {hasFreight && (
        <section aria-label="Freight" className="space-y-2">
          <h4 className="text-xs font-medium">Freight</h4>
          <AmountList
            className="text-xs"
            rows={[
              { label: 'Quoted', amount: freight.quoted_amount },
              { label: 'Booked', amount: freight.booked_amount },
              { label: 'Invoiced', amount: freight.invoiced_amount },
            ]}
          />
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="text-muted-foreground">Freight profit</span>
            <span className={`font-semibold tabular-nums ${freightProfit < 0 ? 'text-destructive' : ''}`}>
              {formatMoney(freightProfit)}
            </span>
          </div>
          <ul className="space-y-2.5 pt-1">
            {freight.shipments.map((shipment) => (
              <li key={shipment.shipment_uuid} className="space-y-0.5 text-xs">
                <div className="flex items-baseline justify-between gap-2">
                  {shipment.purchase_order_uuid ? (
                    <Link
                      to="/purchase-orders/$poId"
                      params={{ poId: shipment.purchase_order_uuid }}
                      className="min-w-0 truncate font-medium text-primary hover:underline"
                    >
                      {shipment.po_number || 'Purchase order'}
                    </Link>
                  ) : (
                    <span className="min-w-0 truncate font-medium">{shipment.po_number || 'Purchase order'}</span>
                  )}
                  <span className="shrink-0 tabular-nums font-medium">
                    {shipment.invoiced_amount == null ? (
                      <span className="font-normal text-muted-foreground">Not invoiced</span>
                    ) : (
                      formatMoney(shipment.invoiced_amount)
                    )}
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-2 text-muted-foreground">
                  <span className="min-w-0 truncate">
                    {[shipment.carrier_name, shipment.pro_number && `PRO ${shipment.pro_number}`]
                      .filter(Boolean)
                      .join(' · ') || 'Carrier pending'}
                  </span>
                  {shipment.booked_amount != null && (
                    <span className="shrink-0 tabular-nums">Booked {formatMoney(shipment.booked_amount)}</span>
                  )}
                </div>
                {shipment.invoices.map((invoice) => (
                  <div key={invoice.invoice_uuid} className="flex">
                    <InvoiceLink invoice={invoice} />
                  </div>
                ))}
              </li>
            ))}
            {freight.project_invoices.map((invoice) => (
              <li key={invoice.invoice_uuid} className="flex items-baseline justify-between gap-2 text-xs">
                <InvoiceLink invoice={invoice} />
                <span className="shrink-0 tabular-nums font-medium">{formatMoney(invoice.amount)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {install.length > 0 && <CostRow label="Install" amount={costs.install_amount} invoices={install} />}
      {storage.length > 0 && <CostRow label="Storage" amount={costs.storage_amount} invoices={storage} />}
    </div>
  )
}
